import { readCsLabel } from './labelUtils';
var LEAF_LABELS = {
    label: 'Popisek',
    labelKey: 'Klíč překladu',
    tag: 'Tag',
    code: 'Kód',
    required: 'Povinné',
    repeatable: 'Opakovatelné',
    defaultVisible: 'Výchozí zobrazení',
    vocabularyType: 'Slovník',
    inputType: 'Typ vstupu',
    placeholder: 'Placeholder',
    colSpan: 'Šířka',
    hideOnCreate: 'Skryté při vytvoření',
    readOnly: 'Jen pro čtení',
    minInstances: 'Min. instancí',
    indicator1: 'Indikátor 1',
    indicator2: 'Indikátor 2',
    indicator1Def: 'Definice indikátoru 1',
    indicator2Def: 'Definice indikátoru 2',
    options: 'Volby indikátoru',
    components: 'Komponenty',
    componentType: 'Typ komponenty',
};
/**
 * Predicate that filters out cascading "wrapper" errors from a oneOf
 * schema rule (the outer "must be valid to one and only one" message
 * and the "X found, Y expected" failures of the non-chosen branch).
 * The remaining errors are the actionable leaf rules that the user
 * can do something about. Exported so inline-error renderers (e.g.
 * FieldPropertyPanel) can apply the same suppression as the bottom
 * summary in FormConfigVisualEditor.
 */
export function isActionableError(err) {
    var m = err.message;
    if (m.includes('must be valid to one and only one schema'))
        return false;
    if (/\b(\w+) found, (\w+) expected\b/.test(m))
        return false;
    return true;
}
/** Convert a JSON pointer path into a Czech breadcrumb using live config labels. */
export function humanizeLocation(path, config) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var segs = path.split('/').filter(Boolean);
    var parts = [];
    var i = 0;
    while (i < segs.length) {
        var key = segs[i];
        var next = segs[i + 1];
        if (key === 'tabs' && next !== undefined) {
            var tab = config.tabs[Number(next)];
            var label = tab ? readCsLabel(tab.label) : "#".concat(next);
            parts.push("Z\u00E1lo\u017Eka \"".concat(label || '?', "\""));
            i += 2;
        }
        else if (key === 'fields' && next !== undefined) {
            var tabIdx = Number(segs[i - 1]);
            var fIdx = Number(next);
            var field = (_a = config.tabs[tabIdx]) === null || _a === void 0 ? void 0 : _a.fields[fIdx];
            var tag = (_b = field === null || field === void 0 ? void 0 : field.tag) !== null && _b !== void 0 ? _b : "#".concat(next);
            parts.push("Pole ".concat(tag));
            i += 2;
        }
        else if (key === 'subfields' && next !== undefined) {
            var tabIdx = Number(segs[i - 3]);
            var fIdx = Number(segs[i - 1]);
            var sIdx = Number(next);
            var sf = (_e = (_d = (_c = config.tabs[tabIdx]) === null || _c === void 0 ? void 0 : _c.fields[fIdx]) === null || _d === void 0 ? void 0 : _d.subfields) === null || _e === void 0 ? void 0 : _e[sIdx];
            // Use `$code` when the live subfield code is known, otherwise
            // fall back to the bare index `#N` (no `$` prefix on an index).
            parts.push(sf ? "Podpole $".concat(sf.code) : "Podpole #".concat(next));
            i += 2;
        }
        else if (key === 'components' && next !== undefined) {
            var tabIdx = Number(segs[i - 5]);
            var fIdx = Number(segs[i - 3]);
            var sIdx = Number(segs[i - 1]);
            var cIdx = Number(next);
            var comp = (_k = (_j = (_h = (_g = (_f = config.tabs[tabIdx]) === null || _f === void 0 ? void 0 : _f.fields[fIdx]) === null || _g === void 0 ? void 0 : _g.subfields) === null || _h === void 0 ? void 0 : _h[sIdx]) === null || _j === void 0 ? void 0 : _j.components) === null || _k === void 0 ? void 0 : _k[cIdx];
            var ct = (_l = comp === null || comp === void 0 ? void 0 : comp.componentType) !== null && _l !== void 0 ? _l : "#".concat(next);
            parts.push("Komponenta ".concat(ct));
            i += 2;
        }
        else if (key === 'options' && next !== undefined) {
            parts.push("Volba ".concat(next));
            i += 2;
        }
        else {
            var propLabel = (_m = LEAF_LABELS[key]) !== null && _m !== void 0 ? _m : key;
            parts.push(propLabel);
            i += 1;
        }
    }
    return parts.length > 0 ? parts.join(' › ') : '(neznámé umístění)';
}
/** Translate a raw networknt message to a Czech actionable phrase. */
export function humanizeMessage(raw, path) {
    var minLen = raw.match(/must be at least (\d+) characters? long/);
    if (minLen) {
        var n = Number(minLen[1]);
        if (n === 1 && path.endsWith('/label'))
            return 'Nesmí být prázdný';
        if (n === 1)
            return 'Nesmí být prázdné';
        return "Mus\u00ED m\u00EDt alespo\u0148 ".concat(n, " znak\u016F");
    }
    var maxLen = raw.match(/must not be longer than (\d+)/);
    if (maxLen)
        return "Nesm\u00ED b\u00FDt del\u0161\u00ED ne\u017E ".concat(maxLen[1], " znak\u016F");
    if (raw.includes('is missing') || raw.includes('is required')) {
        return 'Chybí povinná hodnota';
    }
    if (raw.includes('does not match the pattern')) {
        return 'Hodnota neodpovídá očekávanému formátu';
    }
    if (raw.includes('must be valid to')) {
        return 'Hodnota neodpovídá žádné povolené variantě';
    }
    var typeExpected = raw.match(/(\w+) found, (\w+) expected/);
    if (typeExpected) {
        var found = typeExpected[1], expected = typeExpected[2];
        return "Neplatn\u00FD typ (o\u010Dek\u00E1v\u00E1n ".concat(expected, ", nalezen ").concat(found, ")");
    }
    // Strip the leading "/path/: " prefix the validator sometimes includes
    // so the rendered message doesn't duplicate the path we already show
    // separately.
    return raw.replace(/^[/\w]+:\s*/, '');
}
export function humanizeErrors(errors, config) {
    // Step 1: suppress oneOf cascade noise. The parent "one and only one"
    // wrapper and the "X found, Y expected" alternative-branch failures
    // never carry actionable information — the leaf rule (minLength,
    // required, pattern, ...) on the chosen branch does.
    var filtered = errors.filter(isActionableError);
    // Step 2: dedupe by path — keep first surviving error per path.
    // After filtering oneOf noise the remaining errors per path are
    // already the actionable ones; if multiple share a path the first
    // is generally as good as any (they describe the same problem).
    var seen = new Map();
    for (var _i = 0, filtered_1 = filtered; _i < filtered_1.length; _i++) {
        var err = filtered_1[_i];
        if (!seen.has(err.path))
            seen.set(err.path, err);
    }
    // Step 3 fallback: if filtering ate everything (rare — e.g., the only
    // problem really IS a type mismatch with no inner leaf rule), keep
    // the most-specific (deepest path) original error so the user sees
    // *something* actionable.
    if (seen.size === 0 && errors.length > 0) {
        var deepest = errors.reduce(function (a, b) {
            return a.path.split('/').length >= b.path.split('/').length ? a : b;
        });
        seen.set(deepest.path, deepest);
    }
    return Array.from(seen.values()).map(function (err) { return ({
        location: humanizeLocation(err.path, config),
        message: humanizeMessage(err.message, err.path),
        severity: err.severity,
        raw: err,
    }); });
}
